import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const STYLES = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-orange-50 border-orange-200 text-orange-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

const ICON_STYLES = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-orange-500',
  info: 'text-blue-500',
};

const ToastItem = ({ toast, onRemove }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const enterTimerRef = useRef(null);

  useEffect(() => {
    // Trigger enter animation
    enterTimerRef.current = setTimeout(() => setVisible(true), 10);
    // Auto-dismiss
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(toast?.id), 300);
    }, toast?.duration || 4000);

    return () => {
      clearTimeout(enterTimerRef?.current);
      clearTimeout(timerRef?.current);
    };
  }, [toast?.id, toast?.duration, onRemove]);

  const handleClose = useCallback(() => {
    clearTimeout(timerRef?.current);
    clearTimeout(enterTimerRef?.current);
    setVisible(false);
    setTimeout(() => onRemove(toast?.id), 300);
  }, [toast?.id, onRemove]);

  const ToastIcon = ICONS?.[toast?.type] || ICONS?.info;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg max-w-sm w-full transition-all duration-300 ${
        STYLES?.[toast?.type] || STYLES?.info
      } ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
      }`}
      role="alert"
      aria-live="polite"
    >
      <ToastIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${ICON_STYLES?.[toast?.type] || ICON_STYLES?.info}`} />
      <div className="flex-1 min-w-0">
        {toast?.title && (
          <p className="text-sm font-semibold mb-0.5">{toast?.title}</p>
        )}
        <p className="text-sm leading-snug">{toast?.message}</p>
      </div>
      <button
        onClick={handleClose}
        className="flex-shrink-0 p-0.5 rounded hover:bg-black/10 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = `toast-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    setToasts(prev => [...prev?.slice(-4), { id, type, title, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const toast = {
    success: (message, title, duration) => addToast({ type: 'success', message, title, duration }),
    error: (message, title, duration) => addToast({ type: 'error', message, title, duration: duration || 6000 }),
    warning: (message, title, duration) => addToast({ type: 'warning', message, title, duration }),
    info: (message, title, duration) => addToast({ type: 'info', message, title, duration }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast Container */}
      <div
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
        aria-label="Notifications"
      >
        {toasts?.map(t => (
          <div key={t?.id} className="pointer-events-auto">
            <ToastItem toast={t} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Return a no-op toast if used outside provider (graceful degradation)
    return {
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
    };
  }
  return ctx;
};

export default ToastProvider;
